// =============================================================================
// MeuDriver — Upload da foto de CNH (instrutor)
// -----------------------------------------------------------------------------
// Bucket `cnh` (privado). Path: `{auth.uid()}/cnh.{ext}`.
// Aluno comum NÃO vê este componente — apenas o instrutor logado, em sua
// página de edição de perfil.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, ShieldCheck, Upload, FileBadge } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

type CnhStatus = 'pending' | 'verified' | 'rejected' | 'none'

const STATUS_META: Record<Exclude<CnhStatus, 'none'>, { label: string; cls: string }> = {
  pending:  { label: 'Aguardando análise', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Verificada',         cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejeitada',          cls: 'bg-red-100 text-red-800 border-red-200' },
}

export function InstructorCnhUploadCard() {
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<CnhStatus>('none')
  const [reviewNote, setReviewNote] = useState<string | null>(null)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    if (!user?.id) return
    setLoading(true)
    try {
      // Lê o status do registro do instrutor
      const { data: inst } = await supabase
        .from('instructors')
        .select('cnh_status, cnh_review_note')
        .eq('profile_id', user.id)
        .maybeSingle()
      const cnhStatus = (inst as { cnh_status?: CnhStatus; cnh_review_note?: string | null } | null)?.cnh_status ?? 'none'
      const note = (inst as { cnh_review_note?: string | null } | null)?.cnh_review_note ?? null

      // Tenta listar arquivos no bucket (se houver, gera signed URL).
      // O instrutor lê apenas os próprios pela policy "cnh: instructor read own".
      const { data: files } = await supabase
        .storage
        .from('cnh')
        .list(user.id, { limit: 5 })
      const file = files?.find((f) => f.name.startsWith('cnh.'))

      if (file) {
        const { data: signed } = await supabase
          .storage
          .from('cnh')
          .createSignedUrl(`${user.id}/${file.name}`, 60 * 10) // 10 min
        setSignedUrl(signed?.signedUrl ?? null)
        setStatus(cnhStatus === 'none' ? 'pending' : cnhStatus)
      } else {
        setSignedUrl(null)
        setStatus('none')
      }
      setReviewNote(note)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !user?.id) return

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 5 MB).')
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) {
      toast.error('Use JPG, PNG, WEBP ou PDF.')
      return
    }

    setUploading(true)
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
      const path = `${user.id}/cnh.${ext}`
      const { error: upErr } = await supabase
        .storage
        .from('cnh')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (upErr) throw upErr

      // Volta status para 'pending' (a admin precisa reavaliar).
      const { error: upStatus } = await supabase
        .from('instructors')
        .update({ cnh_status: 'pending', cnh_review_note: null })
        .eq('profile_id', user.id)
      if (upStatus) {
        // Talvez ainda não exista a coluna (migration não aplicada). Não bloqueia.
        console.warn('[cnh] não foi possível atualizar cnh_status:', upStatus.message)
      }

      toast.success('CNH enviada! Aguarde a verificação.')
      await refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar CNH.')
    } finally {
      setUploading(false)
    }
  }

  const showBadge = status !== 'none'

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <FileBadge className="h-4 w-4 text-brand-600" />
          Verificação de CNH
        </CardTitle>
        {showBadge && (
          <Badge variant="outline" className={STATUS_META[status as Exclude<CnhStatus, 'none'>].cls}>
            {STATUS_META[status as Exclude<CnhStatus, 'none'>].label}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Envie uma foto nítida da sua CNH para validarmos sua conta. O documento é
          armazenado de forma criptografada e só você e a equipe MeuDriver têm acesso.
        </p>

        {status === 'rejected' && reviewNote && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <p className="font-medium">Motivo da rejeição:</p>
            <p>{reviewNote}</p>
          </div>
        )}

        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Ver arquivo enviado
          </a>
        )}

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={handleFile}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading || loading}
            onClick={() => fileInputRef.current?.click()}
            className="gap-1.5"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {status === 'none' ? 'Enviar foto da CNH' : 'Substituir foto'}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
